// The range's findings as the workspace's fleet and its triage queue draw
// them: each with its session, machine, severity and category, the verdict
// its verification gave it, and the standards it met. One read per range,
// shared by every reader of it for a minute.
//
// The read is the first 1,000 findings of the range (scopeReviews' tenant-wide
// read) with their verdicts and standard answers. A server-side grid by row
// and time bucket replaces it before the fleet is the board's only overview.
import { useEffect, useMemo, useState } from "react";
import { fetchScopeReviewFindings, reviewsWindow } from "./scopeReviews.js";
import { fetchAnswers, fetchVerdicts, listStandards } from "./findingsTriage.js";
import { hasPermission, useMyPermissions } from "./permissions.js";
import { PERMISSIONS } from "./permissionKeys.js";
import { toApiRange } from "./shell.js";

export const FLEET_FINDINGS_LIMIT = 6000;
const KEEP_MS = 60_000;
const cache = new Map();

// Where a session stands in triage, as the Reviews lens sorts its rows: one
// of its findings met a standard (a person should look); some are not
// verified yet; they stood but met no standard; or none of them stood.
export const QUEUE_SECTIONS = [
  ["met", "Standard met"],
  ["open", "Not verified yet"],
  ["not_met", "Standard not met"],
  ["set_aside", "Set aside"],
];
const SET_ASIDE = new Set(["refuted", "non_adverse", "unverifiable"]);
export function queuePlace(findings = []) {
  if (findings.some((f) => f.met.length)) return "met";
  if (!findings.length || findings.some((f) => !f.verdict)) return "open";
  return findings.every((f) => SET_ASIDE.has(f.verdict)) ? "set_aside" : "not_met";
}
// A session's place in the queue as the board and the search say it.
export const PLACE_OUTCOME = { met: "met", open: "not_verified", not_met: "not_met", set_aside: "set_aside" };
// Where review left each session the findings are on, by its id (a session
// with none, nothing flagged, is not in it).
export function reviewPlaces(findings = []) {
  const by = new Map();
  for (const f of findings) {
    if (!by.has(f.session)) by.set(f.session, []);
    by.get(f.session).push(f);
  }
  return new Map([...by].map(([id, list]) => [id, PLACE_OUTCOME[queuePlace(list)]]));
}

async function read(win, sensitive, signal) {
  // The standards wait on nothing and the answers wait on them, so they are
  // asked for while the findings are read.
  const standardsRead = listStandards({ signal }).catch(() => []);
  const step = await fetchScopeReviewFindings({ sessionIds: null, fromMs: win.fromMs, toMs: win.toMs, limit: FLEET_FINDINGS_LIMIT, signal });
  const rows = step?.rows || [];
  const ids = rows.map((r) => String(r.finding_id));
  const [verdicts, standards] = await Promise.all([
    ids.length ? fetchVerdicts({ ids, reviewWindow: win, sensitive, signal }) : {},
    standardsRead,
  ]);
  const digests = standards.map((s) => s.digest).filter(Boolean);
  // An answer counts only against its finding's verdict (answersFor drops the
  // rest), so only the findings with one are asked about.
  const judged = ids.filter((id) => verdicts?.[id]);
  const answers = judged.length && digests.length
    ? await fetchAnswers({ ids: judged, reviewWindow: win, verdicts, digests, sensitive, signal }).catch(() => ({}))
    : {};
  const nameOf = new Map(standards.map((s) => [s.digest, s.name]));
  const findings = rows.map((r) => {
    const id = String(r.finding_id);
    const met = new Set();
    const asked = new Set();
    for (const [digest, byId] of Object.entries(answers || {})) {
      const answer = byId?.[id];
      if (!answer) continue;
      asked.add(nameOf.get(digest) || "A standard");
      if (answer.escalates) met.add(nameOf.get(digest) || "A standard");
    }
    return {
      id,
      session: String(r.trace_id || "").toLowerCase(),
      endpoint: r.endpoint_id || null,
      title: r.title || "",
      category: r.behaviour_category || "",
      severity: r.behaviour_severity || "other",
      at: Date.parse(r.completed_at) || null,
      verdict: verdicts?.[id]?.verdict || null,
      met: [...met],
      asked: [...asked],
    };
  });
  // Built-in standards counted above, and name what they met, but are not listed.
  return { findings, standards: standards.filter((s) => !s.builtIn), clipped: rows.length >= FLEET_FINDINGS_LIMIT };
}

// The range's findings outside a component: the read a reader of them made
// for it, whichever findings it may see (where each session stands is the
// same in both), else one of its own.
export async function readFleetPlaces(range) {
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  const kept = [`${rangeKey}|1`, `${rangeKey}|0`].map((k) => cache.get(k)).find((e) => e && Date.now() - e.at < KEEP_MS);
  const entry = kept || { at: Date.now(), promise: read(reviewsWindow(toApiRange(range)), false, null) };
  if (!kept) {
    cache.set(`${rangeKey}|0`, entry);
    entry.promise.then((out) => { if (!out.findings.length) cache.delete(`${rangeKey}|0`); }, () => cache.delete(`${rangeKey}|0`));
  }
  return reviewPlaces((await entry.promise).findings);
}

// The range's findings as a page with no review behind it gives them (the
// lanes harness), for every reader of them, kept for as long as the page is.
export function _seedFleetFindingsForTests(range, findings) {
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  const entry = { at: Number.MAX_SAFE_INTEGER, promise: Promise.resolve({ findings, standards: [], clipped: false }) };
  cache.set(`${rangeKey}|0`, entry);
  cache.set(`${rangeKey}|1`, entry);
}

const LOADING = { status: "loading", findings: [], standards: [], clipped: false };
const IDLE = { status: "idle", findings: [], standards: [], clipped: false };
// `enabled`: false reads nothing (a reader that needs the findings only for
// some of what it shows).
export function useFleetFindings(range, { enabled = true } = {}) {
  const caps = useMyPermissions();
  const sensitive = hasPermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const win = useMemo(() => reviewsWindow(toApiRange(range)), [rangeKey]);
  // Keyed by the range as picked, not by its bounds: a range that ends now
  // has new bounds for every reader that asks a moment apart, and they are
  // the same read for as long as one is kept.
  const key = `${rangeKey}|${sensitive ? 1 : 0}`;
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const hit = cache.get(key);
    const entry = hit && Date.now() - hit.at < KEEP_MS ? hit : { at: Date.now(), promise: read(win, sensitive, null) };
    cache.set(key, entry);
    entry.promise.then(
      (out) => {
        // Nothing read is not kept: it may be a read made before the
        // session was signed in.
        if (!out.findings.length) cache.delete(key);
        if (live) setState({ key, status: "done", ...out });
      },
      (error) => { cache.delete(key); if (live) setState({ key, status: "error", findings: [], standards: [], clipped: false, error }); },
    );
    return () => { live = false; };
  }, [key, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  // Another range's findings are not this one's: until its own are read, it
  // is loading.
  if (!enabled) return IDLE;
  return state?.key === key ? state : LOADING;
}
